import { Box, SxProps, Typography, Stack, Button } from '@mui/material';
import Loading from '../../General/Loading/Loading';
import React, { Dispatch, useEffect } from 'react';
import { flexBase, fullSize } from '../../../Constants';
import { useParams } from 'react-router-dom';
import { IAppReduxStore } from '../../../redux/store';
import { openDocumentFile } from '../../../redux/project/file/sliceProjectFileOpen';
import { getProjectWorkflowHistory } from '../../../redux/project/sliceProjectWorkflowHistory';
import { useDispatch, useSelector } from 'react-redux';
import { vars } from '../../../Theme';
import { getProjectWorkflowInitial } from '../../../redux/project/sliceProjectWorkflowInitial';
import { openProjectDocumentationItem } from '../../../redux/project/sliceProjectDocumentation';
import { useTranslation } from 'react-i18next';
import { decodeBase64String } from '../../../util/StringUtils';
import { getProjectLogOpen } from '../../../redux/general/sliceDatabaseAccess';
import DatabaseAccessDialog from '../../General/Dialogs/DatabaseAccessDialog/DatabaseAccessDialog';
import { compareDocuments } from '../../../redux/project/documents/sliceProjectDocumentsComparison';

/** Styles for the Typography component that displays file's name. */
const fileNameSx: SxProps = {
    wordWrap: 'break-word',
    textAlign: 'center',
};

/**Create a new instance of BroadcastChannel to communicate between different documents */
const bc = new BroadcastChannel('project_view');

export enum SliceNameEnum {
    projectFileOpen = 'projectFileOpen',
    workflowHistory = 'workflowHistory',
    projectWorkflowInitial = 'projectWorkflowInitial',
    projectDocumentation = 'projectDocumentation',
    projectDocumentsComparison = 'projectDocumentsComparison',
}

const SliceCallbackMapper = {
    [SliceNameEnum.projectFileOpen]: openDocumentFile,
    [SliceNameEnum.workflowHistory]: getProjectWorkflowHistory,
    [SliceNameEnum.projectWorkflowInitial]: getProjectWorkflowInitial,
    [SliceNameEnum.projectDocumentation]: openProjectDocumentationItem,
    [SliceNameEnum.projectDocumentsComparison]: compareDocuments,
};

export default ({ sliceName }: IDocumentLoaderProps) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'documentLoaderView' });

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Get URL variables from the react router. */
    const {
        replicaId,
        universalId,
        docNumber,
        fileName: encodedFileName,
        documentFileInfo: encodedDocumentFileInfo,
    } = useParams();

    /** Decode document file info object. */
    const documentFileInfo = JSON.parse(
        decodeBase64String(encodedDocumentFileInfo) || '{}',
    ) as IDocumentFileInfo;

    const fileName = encodedFileName && decodeBase64String(encodedFileName);
    const documentNumber = documentFileInfo?.documentNumber;

    /** Access relevant state variables from the redux store. */
    const documentUrl = useSelector((store: IAppReduxStore) => store[sliceName].data);
    const documentError = useSelector((store: IAppReduxStore) => store[sliceName].error);

    const { isDialogOpen: isDatabaseAccessDialogOpen, canAccessDatabase } = useSelector(
        (store: IAppReduxStore) => store.databaseAccess,
    );

    /**
     * Dispatchs the OpenDocumentFile for the selected document main file or the workflow history link.
     */
    const callbackOpenDocument = () =>
        dispatch(
            SliceCallbackMapper[sliceName]({
                replicaId,
                universalId,
                fileName,
                fieldName: documentFileInfo?.fieldName,
                fieldIndex: documentFileInfo.fieldIndex,
            }),
        );

    /**
     * Calls callbackOpenDocument when the page is opened.
     */
    useEffect(() => {
        dispatch(getProjectLogOpen({ replicaId }));
    }, []);

    useEffect(() => {
        if (canAccessDatabase) {
            callbackOpenDocument();
        }
    }, [canAccessDatabase]);

    /**
     * Sends errors to the main page.
     */
    useEffect(() => {
        if (sliceName === SliceNameEnum.projectFileOpen) {
            documentError && bc.postMessage(fileName || documentNumber);
        }
    }, [documentError]);

    /**
     * Opens specified document
     */
    useEffect(() => {
        documentUrl && window.location.replace(documentUrl);
    }, [documentUrl]);

    const SliceTranslationsMapper = {
        [SliceNameEnum.projectFileOpen]: {
            errorTitle: t('errorViewDocument'),
            errorDocumentName: `${fileName || documentNumber}`,
            loadingMessage: `${t('preparingDocument')}: ${fileName || documentNumber}`,
        },
        [SliceNameEnum.workflowHistory]: {
            errorTitle: t('errorViewWorkflowHistory'),
            errorDocumentName: `${docNumber}`,
            loadingMessage: `${t('preparingWorkflowHistory')}: ${docNumber}`,
        },
        [SliceNameEnum.projectWorkflowInitial]: {
            errorTitle: t('errorViewWorkflowInitial'),
            errorDocumentName: `${fileName}`,
            loadingMessage: `${t('preparingWorkflowInitial')}: ${fileName}`,
        },
        [SliceNameEnum.projectDocumentation]: {
            errorTitle: t('errorViewDocument'),
            errorDocumentName: `${fileName}`,
            loadingMessage: `${t('preparingDocument')}: ${fileName}`,
        },
        [SliceNameEnum.projectDocumentsComparison]: {
            errorTitle: t('errorDocumentsComparison'),
            errorDocumentName: t('documentsComparison'),
            loadingMessage: t('preparingDocumentsComparison'),
        },
    };

    return (
        <Box
            sx={
                {
                    ...flexBase,
                    ...fullSize,
                    position: 'relative',
                    justifyContent: 'center',
                    px: 1.5,
                } as SxProps
            }
        >
            {documentError ? (
                <Stack sx={{ ...flexBase, justifyContent: 'center' }} spacing={2}>
                    <Typography variant='h6' sx={{ color: vars.errorRed }}>
                        {SliceTranslationsMapper[sliceName].errorTitle}
                    </Typography>

                    <Typography variant='h6' sx={fileNameSx}>
                        {SliceTranslationsMapper[sliceName].errorDocumentName}
                    </Typography>

                    <Typography>{t('errorViewDocumentMessage')}</Typography>

                    <Button onClick={() => window.close()}>{t('closeWindow')}</Button>
                </Stack>
            ) : (
                <Loading message={SliceTranslationsMapper[sliceName].loadingMessage} />
            )}
            {isDatabaseAccessDialogOpen && <DatabaseAccessDialog />}
        </Box>
    );
};

export interface IDocumentLoaderProps {
    sliceName: SliceNameEnum;
}

export interface IDocumentFileInfo {
    /** Document DocNumber. */
    documentNumber: string;

    /** Field name. */
    fieldName: string;

    /** Field index. */
    fieldIndex: string;
}
